import { createSvgEl } from '@learnforge/demo-render';
import { type Expr, mul } from '../algebra/expr';
import { format } from '../algebra/parse';
import { Polynomial } from '../algebra/polynomial';
import { autoSimplify } from '../algebra/simplify';

export interface TermGridOptions {
  rowLabels: readonly Expr[];
  colLabels: readonly Expr[];
  highlightByDegree: boolean;
  width: number;
  height: number;
  onCellClick?(row: number, col: number): void;
}

export interface TermGridHandle {
  group: SVGGElement;
  update(opts: Partial<TermGridOptions>): void;
  destroy(): void;
}

export function createTermGrid(
  parent: SVGSVGElement | SVGGElement,
  opts: TermGridOptions,
): TermGridHandle {
  const group = createSvgEl('g');
  parent.appendChild(group);

  const currentOpts = {
    rowLabels: Array.from(opts.rowLabels),
    colLabels: Array.from(opts.colLabels),
    highlightByDegree: opts.highlightByDegree,
    width: opts.width,
    height: opts.height,
    onCellClick: opts.onCellClick
      ? (row: number, col: number) => opts.onCellClick!(row, col)
      : undefined,
  };

  function renderGrid(): void {
    // Clear the group
    while (group.firstChild) {
      group.removeChild(group.firstChild);
    }

    const m = currentOpts.rowLabels.length;
    const n = currentOpts.colLabels.length;

    // Compute cell dimensions
    const cellWidth = currentOpts.width / (n + 1);
    const cellHeight = currentOpts.height / (m + 1);

    // Render column headers
    for (let col = 0; col < n; col++) {
      const colLabel = currentOpts.colLabels[col]!;
      const x = (col + 1) * cellWidth;
      const y = cellHeight / 2;

      const text = createSvgEl('text', {
        x: x + cellWidth / 2,
        y,
        'text-anchor': 'middle',
        'dominant-baseline': 'middle',
        class: 'alg-title',
      });
      text.textContent = format(colLabel, { useUnicodeSuperscripts: true });
      group.appendChild(text);
    }

    // Render row headers
    for (let row = 0; row < m; row++) {
      const rowLabel = currentOpts.rowLabels[row]!;
      const x = cellWidth / 2;
      const y = (row + 1) * cellHeight;

      const text = createSvgEl('text', {
        x,
        y: y + cellHeight / 2,
        'text-anchor': 'middle',
        'dominant-baseline': 'middle',
        class: 'alg-title',
      });
      text.textContent = format(rowLabel, { useUnicodeSuperscripts: true });
      group.appendChild(text);
    }

    // Render interior cells
    for (let row = 0; row < m; row++) {
      for (let col = 0; col < n; col++) {
        const rowLabel = currentOpts.rowLabels[row]!;
        const colLabel = currentOpts.colLabels[col]!;

        // Compute product
        const product = mul(rowLabel, colLabel);

        // Simplify product
        const simplified = autoSimplify(product).result;

        // Format product
        const productStr = format(simplified, { useUnicodeSuperscripts: true });

        // Compute degree
        let degree: number | null = null;
        const poly = Polynomial.fromExpr(simplified, 'x');
        if (poly !== null && isFinite(poly.degree)) {
          degree = poly.degree;
        }

        // Position
        const x = (col + 1) * cellWidth;
        const y = (row + 1) * cellHeight;

        // Create cell group
        const cellGroup = createSvgEl('g', {
          'data-row': String(row),
          'data-col': String(col),
          ...(degree !== null && { 'data-degree': String(degree) }),
          ...(degree !== null && currentOpts.highlightByDegree && { class: `degree-${degree}` }),
        });

        // Create cell background (optional, helps with clicking)
        const rect = createSvgEl('rect', {
          x: x + 5,
          y: y + 5,
          width: cellWidth - 10,
          height: cellHeight - 10,
          class:
            degree !== null && currentOpts.highlightByDegree
              ? `alg-grid-cell degree-${degree}`
              : 'alg-grid-cell',
        });
        cellGroup.appendChild(rect);

        // Create text element
        const text = createSvgEl('text', {
          x: x + cellWidth / 2,
          y: y + cellHeight / 2,
          'text-anchor': 'middle',
          'dominant-baseline': 'middle',
          class: 'alg-text',
        });
        text.textContent = productStr;
        cellGroup.appendChild(text);

        // Add click listener
        if (currentOpts.onCellClick) {
          cellGroup.addEventListener('click', () => {
            currentOpts.onCellClick?.(row, col);
          });
        }

        group.appendChild(cellGroup);
      }
    }
  }

  renderGrid();

  const handle: TermGridHandle = {
    group,

    update(partial: Partial<TermGridOptions>): void {
      if (partial.rowLabels !== undefined) {
        currentOpts.rowLabels = Array.from(partial.rowLabels);
      }
      if (partial.colLabels !== undefined) {
        currentOpts.colLabels = Array.from(partial.colLabels);
      }
      if (partial.highlightByDegree !== undefined) {
        currentOpts.highlightByDegree = partial.highlightByDegree;
      }
      if (partial.width !== undefined) {
        currentOpts.width = partial.width;
      }
      if (partial.height !== undefined) {
        currentOpts.height = partial.height;
      }
      if (partial.onCellClick !== undefined) {
        currentOpts.onCellClick = partial.onCellClick;
      }
      renderGrid();
    },

    destroy(): void {
      if (group.parentNode) {
        group.parentNode.removeChild(group);
      }
    },
  };

  return handle;
}
